import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS, waLink } from "../../data/content";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 z-50 w-full"
    >
      <div
        className={`mx-auto flex max-w-[1400px] items-center justify-between gap-6 px-5 transition-[padding,background-color,box-shadow,backdrop-filter] duration-500 md:px-8 ${
          scrolled
            ? "my-3 rounded-full border border-white/50 bg-white/70 py-3 shadow-[0_8px_30px_rgba(45,40,48,0.06)] backdrop-blur-xl"
            : "py-6"
        }`}
      >
        <Logo />

        <nav className="hidden items-center gap-8 lg:flex">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`nav-${link.href.replace("#", "")}`}
              className="group relative text-sm font-medium text-pblv-inksoft transition-colors duration-300 hover:text-pblv-ink"
            >
              {link.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-pblv-mauve transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="#top"
            data-testid="header-pblv971-logo"
            aria-label="Association Plus Belle La Vie 971"
            className="hidden shrink-0 md:inline-flex"
          >
            <img
              src="/logo-pblv971-new.png"
              alt="Association Plus Belle La Vie 971"
              className="h-14 w-auto object-contain md:h-16"
              loading="eager"
            />
          </a>
          <a
            href={waLink("Bonjour, je souhaite des informations sur le réseau T'POZ.")}
            target="_blank"
            rel="noreferrer"
            data-testid="header-contact-cta"
            className="group hidden items-center gap-2 rounded-full bg-pblv-ink px-5 py-2.5 text-sm font-semibold text-pblv-bg transition-all duration-300 hover:-translate-y-0.5 hover:bg-pblv-mauve sm:inline-flex"
          >
            <MessageCircle className="h-4 w-4" strokeWidth={1.8} />
            Nous contacter
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-pblv-ink/10 bg-white/60 text-pblv-ink backdrop-blur transition-colors hover:bg-white lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
            className="mx-4 mt-2 rounded-3xl border border-white/50 bg-white/90 p-5 shadow-xl backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${link.href.replace("#", "")}`}
                  className="rounded-xl px-4 py-3 text-base font-medium text-pblv-ink transition-colors hover:bg-pblv-bg2"
                >
                  {link.label}
                </a>
              ))}
              <a
                href={waLink("Bonjour, je souhaite des informations sur le réseau T'POZ.")}
                target="_blank"
                rel="noreferrer"
                onClick={() => setOpen(false)}
                data-testid="mobile-contact-cta"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-pblv-ink px-5 py-3 text-sm font-semibold text-pblv-bg"
              >
                <MessageCircle className="h-4 w-4" /> Nous contacter
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
